<script lang="ts" setup>
import type { ISelectItem } from "@/types"
import { computed } from "vue"
import { Select } from "@/components/ui"
import { LOCALE_CODES } from "./localeCodes"

interface LocaleComboboxProps {
  name: string
  label: string
  hint?: string
  error?: string
  disabled?: boolean
}

withDefaults(defineProps<LocaleComboboxProps>(), {
  hint: "",
  error: "",
  disabled: false,
})

function createDisplayNames(): Intl.DisplayNames | null {
  // Try catch in order to check if the browser supports the call below
  try {
    return new Intl.DisplayNames([navigator.language, "en"], { type: "language" })
  }
  catch {
    return null
  }
}

function getDisplayName(displayNames: Intl.DisplayNames | null, code: string): string {
  if (!displayNames)
    return code
  try {
    const name = displayNames.of(code)
    return (name && name !== code) ? `${name} (${code})` : code
  }
  catch {
    return code
  }
}

const allItems = computed<ISelectItem<string>[]>(() => {
  const displayNames = createDisplayNames()
  return LOCALE_CODES
    .map(code => ({ value: code, label: getDisplayName(displayNames, code) }))
    .sort((a, b) => a.label.localeCompare(b.label))
})
</script>

<template>
  <Select
    :name="name"
    :label="label"
    :items="allItems"
    :hint="hint"
    :error="error"
    :disabled="disabled"
    search
    data-test="locale-combobox"
  />
</template>
